import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Loader2, DollarSign, AlertCircle, CheckCircle2, Clock, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import BookingDetailModal from './BookingDetailModal';
import PageHelp from './help/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(cents) {
  const v = (cents || 0) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

/**
 * Payment Summary Card for provider dashboard home.
 *
 * Shows aggregate totals across CONFIRMED + COMPLETED bookings:
 *   - Total Owed (UNPAID + PARTIAL balance)
 *   - Total Collected
 *   - Bucket counts: Unpaid / Partial / Paid
 * Clicking "View unpaid" opens a list of bookings with an outstanding balance.
 * Each row is clickable to open the BookingDetailModal where the provider can
 * enter or update payment amounts.
 */
export default function PaymentSummaryCard() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnpaid, setShowUnpaid] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('OUTSTANDING'); // OUTSTANDING|UNPAID|PARTIAL|PAID|NEEDS_INVOICE
  const [unpaid, setUnpaid] = useState([]);
  const [paid, setPaid] = useState([]);
  const [needsInvoice, setNeedsInvoice] = useState([]);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/payment-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data);
    } catch (e) {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUnpaid = async () => {
    setUnpaidLoading(true);
    try {
      const [up, allBk, ni] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/unpaid-bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/dashboard/bookings`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'COMPLETED' },
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/dashboard/needs-invoice-bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);
      setUnpaid(up.data || []);
      // Paid bookings = CONFIRMED/COMPLETED with paymentStatus PAID
      const allBookings = (allBk.data || []);
      setPaid(allBookings.filter(b => (b.paymentStatus || '').toUpperCase() === 'PAID'));
      setNeedsInvoice(ni.data || []);
    } catch (e) {
      setUnpaid([]);
      setPaid([]);
      setNeedsInvoice([]);
    } finally {
      setUnpaidLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Listen for an app-wide "open the Needs Invoice queue" event so the Field
  // View chip can pop this card's dialog without coupling via URL params or
  // refs.
  useEffect(() => {
    const handler = () => openUnpaid('NEEDS_INVOICE');
    window.addEventListener('rw:open-needs-invoice', handler);
    return () => window.removeEventListener('rw:open-needs-invoice', handler);
  }, []);

  const openUnpaid = (filter = 'OUTSTANDING') => {
    setPaymentFilter(filter);
    setShowUnpaid(true);
    loadUnpaid();
  };

  const onModalUpdated = () => {
    load();
    if (showUnpaid) loadUnpaid();
  };

  if (loading) {
    return (
      <Card data-testid="payment-summary-card">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!summary) return null;

  const unpaidCount = summary.buckets.UNPAID.count || 0;
  const partialCount = summary.buckets.PARTIAL.count || 0;
  const paidCount = summary.buckets.PAID.count || 0;
  const needsInvoiceCount = summary.needsInvoiceCount || 0;
  const totalOwed = summary.totalOwedCents || 0;
  const totalPaid = summary.totalPaidCents || 0;
  const hasOutstanding = unpaidCount + partialCount > 0;

  return (
    <>
      <Card data-testid="payment-summary-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-xl flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              Payment Tracker
              <PageHelp
                testId="payment-summary"
                title="Payment Tracker"
                purpose="See who has paid, who still owes, and what needs an invoice."
                actions={[
                  { label: 'Total Owed', desc: 'money still coming to you' },
                  { label: 'Collected', desc: "money you've been paid" },
                  { label: 'Needs Invoice', desc: 'finished jobs you still need to bill' },
                ]}
                nextStep="Tap any box to see the list and record a payment."
              />
            </CardTitle>
            {hasOutstanding && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openUnpaid('OUTSTANDING')}
                data-testid="view-unpaid-btn"
              >
                View unpaid ({unpaidCount + partialCount})
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Top-line totals — clickable */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => openUnpaid('OUTSTANDING')}
              className="rounded-lg border bg-red-50 border-red-200 p-3 text-left hover:bg-red-100 hover:border-red-300 transition"
              data-testid="payment-total-owed"
            >
              <p className="text-xs font-medium text-red-700 uppercase tracking-wide flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" /> Total Owed
              </p>
              <p className="text-2xl font-bold text-red-900 mt-1">{fmtMoney(totalOwed)}</p>
              <p className="text-xs text-red-700/80 mt-0.5">
                {unpaidCount + partialCount} booking{unpaidCount + partialCount === 1 ? '' : 's'}
              </p>
            </button>
            <button
              type="button"
              onClick={() => openUnpaid('PAID')}
              className="rounded-lg border bg-green-50 border-green-200 p-3 text-left hover:bg-green-100 hover:border-green-300 transition"
              data-testid="payment-total-collected"
            >
              <p className="text-xs font-medium text-green-700 uppercase tracking-wide flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Collected
              </p>
              <p className="text-2xl font-bold text-green-900 mt-1">{fmtMoney(totalPaid)}</p>
              <p className="text-xs text-green-700/80 mt-0.5">
                {paidCount} paid in full
              </p>
            </button>
          </div>

          {/* Buckets row — each clickable */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t">
            <BucketStat
              label="Unpaid"
              count={unpaidCount}
              amount={summary.buckets.UNPAID.totalBalanceCents}
              color="red"
              testid="bucket-unpaid"
              onClick={() => openUnpaid('UNPAID')}
            />
            <BucketStat
              label="Partial"
              count={partialCount}
              amount={summary.buckets.PARTIAL.totalBalanceCents}
              color="amber"
              testid="bucket-partial"
              onClick={() => openUnpaid('PARTIAL')}
            />
            <BucketStat
              label="Paid"
              count={paidCount}
              amount={summary.buckets.PAID.totalPaidCents}
              color="green"
              testid="bucket-paid"
              onClick={() => openUnpaid('PAID')}
            />
            <BucketStat
              label="Needs Invoice"
              count={needsInvoiceCount}
              amount={null}
              color="amber"
              testid="bucket-needs-invoice"
              onClick={() => openUnpaid('NEEDS_INVOICE')}
            />
          </div>

          {!hasOutstanding && (
            <p className="text-sm text-muted-foreground text-center pt-2">
              No outstanding balances. You&apos;re all caught up.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Filtered bookings dialog */}
      <Dialog open={showUnpaid} onOpenChange={setShowUnpaid}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          data-testid="unpaid-bookings-dialog"
        >
          <DialogHeader>
            <DialogTitle data-testid="payment-dialog-title">
              {paymentFilter === 'PAID' ? 'Paid bookings' :
               paymentFilter === 'UNPAID' ? 'Unpaid bookings' :
               paymentFilter === 'PARTIAL' ? 'Partial payments' :
               paymentFilter === 'NEEDS_INVOICE' ? 'Needs invoice' :
               'Outstanding balances'}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const rows = paymentFilter === 'PAID'
              ? paid
              : paymentFilter === 'NEEDS_INVOICE'
                ? needsInvoice
                : paymentFilter === 'OUTSTANDING'
                  ? unpaid
                  : unpaid.filter(b => (b.paymentStatus || '').toUpperCase() === paymentFilter);
            if (unpaidLoading) {
              return (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              );
            }
            if (!rows.length) {
              return (
                <p className="text-center text-muted-foreground p-6" data-testid="payment-dialog-empty">
                  {paymentFilter === 'PAID' ? 'No paid bookings yet.' : 'Nothing in this bucket.'}
                </p>
              );
            }
            return (
              <div className="space-y-2">
                {rows.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setSelectedBookingId(b.id)}
                  className="w-full text-left border rounded-lg p-3 hover:bg-muted transition-colors flex items-center justify-between gap-3"
                  data-testid={`unpaid-row-${b.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{b.clientName || (b.client && b.client.name) || 'Client'}</p>
                      <Badge
                        className={
                          (b.paymentStatus || '').toUpperCase() === 'PAID'
                            ? 'bg-green-100 text-green-800 border-green-300 text-xs'
                            : (b.paymentStatus || '').toUpperCase() === 'PARTIAL'
                              ? 'bg-amber-100 text-amber-800 border-amber-300 text-xs'
                              : 'bg-red-100 text-red-700 border-red-300 text-xs'
                        }
                      >
                        {b.paymentStatus || 'UNPAID'}
                      </Badge>
                      {b.needsInvoice && (
                        <Badge
                          className="bg-amber-100 text-amber-800 border border-amber-300 text-xs"
                          data-testid={`row-needs-invoice-${b.id}`}
                        >
                          Invoice
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3" />
                      {b.startDateTime && format(parseISO(b.startDateTime), 'MMM d, yyyy')}
                      {b.serviceName ? ` • ${b.serviceName}` : (b.service && b.service.name ? ` • ${b.service.name}` : '')}
                      {b.horseCount ? ` • ${b.horseCount} animal${b.horseCount === 1 ? '' : 's'}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${(b.paymentStatus || '').toUpperCase() === 'PAID' ? 'text-green-700' : 'text-red-700'}`}>
                      {fmtMoney(((b.paymentStatus || '').toUpperCase() === 'PAID' ? (b.amountPaidCents || b.totalCents || 0) : (b.balanceCents || 0)))}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {(b.paymentStatus || '').toUpperCase() === 'PAID' ? 'collected' : 'balance due'}
                    </p>
                  </div>
                </button>
                ))}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {selectedBookingId && (
        <BookingDetailModal
          bookingId={selectedBookingId}
          open={!!selectedBookingId}
          onOpenChange={(o) => !o && setSelectedBookingId(null)}
          onUpdated={onModalUpdated}
        />
      )}
    </>
  );
}

function BucketStat({ label, count, amount, color, testid, onClick }) {
  const colorMap = {
    red: 'text-red-700',
    amber: 'text-amber-700',
    green: 'text-green-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-center rounded-md p-2 hover:bg-muted transition cursor-pointer"
      data-testid={testid}
    >
      <p className={`text-xs font-medium ${colorMap[color]} uppercase tracking-wide`}>{label}</p>
      <p className="text-lg font-bold mt-0.5">{count}</p>
      {amount != null && <p className="text-xs text-muted-foreground">{fmtMoney(amount)}</p>}
    </button>
  );
}
